<script lang="ts">
	import type { PoiImages } from '$lib/components/pois/types';
	import type { ClassValue } from 'svelte/elements';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import PoiImage from '$lib/components/pois/image/PoiImage.svelte';
	import { PREFETCH_IMAGES_COUNT } from '$lib/constants/poilist';
	import * as m from '$paraglide/messages';
	import { debounce } from 'throttle-debounce';

	const DEBOUNCE_USER_RESIZE = 100;
	const DEBOUNCE_USER_SCROLL = 10;

	interface Props {
		images: PoiImages[];
		prefixAlt?: string;
		carouselClasses?: ClassValue;
		containerClasses?: ClassValue;
		controlsClasses?: ClassValue;
		withEagerImages?: boolean;
		fallbackSrc?: string;
		// Note: don't set ariaLabel to 'carousel' as aria-roledescription is already set to 'carousel', which will be redundant.
		ariaLabel?: string;
		onImageScroll?: () => void;
	}

	const {
		images,
		prefixAlt,
		carouselClasses,
		containerClasses,
		controlsClasses,
		withEagerImages = false,
		fallbackSrc,
		ariaLabel = 'image carousel',
		onImageScroll,
	}: Props = $props();

	let currentIndex = $state(0);
	let carouselContainer: HTMLElement | undefined = $state();

	const handleScroll = debounce(DEBOUNCE_USER_SCROLL, (e) => {
		e.preventDefault();

		if (carouselContainer) {
			const scrollLeft = carouselContainer.scrollLeft;
			const imageWidth = carouselContainer.getBoundingClientRect().width;
			const newIndex = Math.round(scrollLeft / imageWidth);
			currentIndex = newIndex;
		}
	});

	const handleResize = debounce(DEBOUNCE_USER_RESIZE, () => {
		scrollToIndexImage(currentIndex);
	});

	function scrollImage(direction: 'right' | 'left' = 'right') {
		if (carouselContainer) {
			const carouselContainerWidth = carouselContainer.getBoundingClientRect().width;
			let scrollDirectionIndex = 0;

			if (direction === 'right') {
				scrollDirectionIndex = currentIndex + 1;
			}
			if (direction === 'left') {
				scrollDirectionIndex = currentIndex - 1;
			}

			carouselContainer.scrollTo({
				left: carouselContainerWidth * scrollDirectionIndex,
				behavior: 'smooth',
			});
		}
	}

	function scrollToIndexImage(index: number) {
		if (carouselContainer) {
			const carouselContainerWidth = carouselContainer.getBoundingClientRect().width;
			carouselContainer.scrollTo({
				left: 0,
				behavior: 'instant',
			});

			carouselContainer.scrollTo({
				left: carouselContainerWidth * index,
				behavior: 'instant',
			});
		}
	}

	function abstractPreventDefault(e: Event) {
		// Kind of hackish but this solve strange behavior when clicking on the carousel controls which trigger the SearchPOIs query...
		// After some reasearch, whithout this, the cta bubble until triggering the handleRemoveItemStored function in the HistoryResult component.
		e.preventDefault();
	}
</script>

<svelte:window onresize={handleResize} />

<div
	class={['container-carousel w-full relative cursor-pointer', containerClasses]}
	role="region"
	aria-roledescription="carousel"
	aria-label={ariaLabel}
>
	<div class="h-auto">
		<Button
			color="white"
			icon="chevron-left"
			size="md"
			variant="ghost"
			ariaLabel={m.poi_card__carousel__prev_button()}
			ariaHidden={currentIndex === 0}
			class={['left-arrow-container absolute left-0 h-full', !(currentIndex > 0) && 'hidden', controlsClasses]}
			onclick={(e) => {
				e.preventDefault();
				scrollImage('left');
			}}
			onmousedown={event => abstractPreventDefault(event)}
			onpointerdown={event => abstractPreventDefault(event)}
		/>

		<ul
			class={['carousel flex h-100% overflow-x-scroll m-0 p-0 bg-neutral-weakest list-none', carouselClasses]}
			aria-roledescription="slide"
			role="group"
			aria-label={m.poi_card__carousel__list_aria_label({
				current: currentIndex + 1,
				total: images.length,
			})}
			onscroll={handleScroll}
			onscrollend={() => onImageScroll?.()}
			bind:this={carouselContainer}
		>
			{#each images as image, index (`${image}-${index}`)}
				<li class="img-container items-center flex grow-0 shrink-0 justify-center w-full">
					<figure class="img-wrapper w-full h-full overflow-hidden m-0 p-0">
						<PoiImage
							srcset={image}
							eager={withEagerImages ? index < PREFETCH_IMAGES_COUNT : false}
							alt={`${prefixAlt} - ${index}`}
							class="h-full w-full"
							objectFit="cover"
							height="132px"
							width="260px"
							{fallbackSrc}
							sizes="320px"
						/>
					</figure>
				</li>
			{/each}
		</ul>

		<Button
			color="white"
			icon="chevron-right"
			size="md"
			variant="ghost"
			ariaLabel={m.poi_card__carousel__next_button()}
			ariaHidden={!(currentIndex < images.length - 1 && images.length > 1)}
			class={['right-arrow-container absolute right-0 h-full poi-card-carousel', !(currentIndex < images.length - 1 && images.length > 1) && 'hidden', controlsClasses]}
			onclick={(e) => {
				e.preventDefault();
				scrollImage('right');
			}}
			onmousedown={event => abstractPreventDefault(event)}
			onpointerdown={event => abstractPreventDefault(event)}
		/>
	</div>
</div>

<style>
	.carousel {
		-ms-overflow-style: none;
		scrollbar-width: none;
		scroll-snap-type: x mandatory;
	}

	.carousel::-webkit-scrollbar {
		display: none;
	}

	.carousel :global(.left-arrow-container),
	.carousel :global(.right-arrow-container) {
		top: 50%;
		transform: translateY(-50%);

	}

	.carousel :global(.left-arrow-container) {
		background: linear-gradient(90deg, rgba(29, 49, 64, 0.5) 0%, rgba(29, 49, 64, 0) 100%);
	}
	.carousel :global(.right-arrow-container) {
		background: linear-gradient(-90deg, rgba(29, 49, 64, 0.5) 0%, rgba(29, 49, 64, 0) 100%);
	}

	.img-container {
		scroll-snap-align: start;
	}

	@media (hover: hover) and (pointer: fine) {
		.carousel :global(.left-arrow-container:not(.hidden)),
		.carousel :global(.right-arrow-container:not(.hidden)) {
			display: block;
		}
	}
</style>
